import { useState } from "react";
import { EDITOR_CANVAS, FloorPlan, positionsOf } from "@/components/FloorPlan";
import { TableNames } from "@/components/TableNames";
import { saveLayout } from "@/lib/store";
import type { TableConfig } from "@/lib/types";

/**
 * 場地平面配置編輯器：把桌子拖到對應真實餐廳格局的位置，並改桌號與人數。
 *
 * 這裡的拖曳只送座標 —— 後端的 layout 端點也只接受座標，兩邊是一致的。
 * 桌號與座位數走各自的端點（見 TableNames）：它們會動到已選好的位子，
 * 需要在同一個交易裡連帶處理，不能跟「搬桌子」混在一起送。
 */
export function LayoutEditor({ code, tables }: { code: string; tables: TableConfig[] }) {
  const [pos, setPos] = useState(() => positionsOf(tables));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const move = (label: string, x: number, y: number) => {
    setPos((p) => ({ ...p, [label]: { x, y } }));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    // 沒在座標表裡的桌子（例如剛改完名字、還沒重抓資料）退回格線位置，
    // 而不是讓 undefined 一路傳進 API
    const grid = positionsOf(tables);
    const ok = await saveLayout(
      code,
      tables.map((t) => {
        const p = pos[t.label] ?? grid[t.label];
        return { label: t.label, x: p.x, y: p.y };
      }),
    );
    setBusy(false);
    if (!ok) {
      setError("儲存失敗，請再試一次。");
      return;
    }
    setDirty(false);
    setSaved(true);
  };

  const reset = () => {
    setPos(positionsOf(tables));
    setDirty(true);
    setSaved(false);
  };

  /**
   * 桌號改了，座標表的 key 也要跟著改 —— 這份表是用桌號當 key 的，
   * 不換的話那張桌子會查不到座標，畫面上跳到正中央，儲存時還會拿到 undefined。
   */
  const renamed = (from: string, to: string) => {
    setPos((p) => {
      const { [from]: moved, ...rest } = p;
      return moved ? { ...rest, [to]: moved } : p;
    });
  };

  return (
    <div>
      <FloorPlan
        tables={tables}
        positions={pos}
        editable
        onMove={move}
        tableWidth={(t) => 56 + Math.min(t.seats, 20) * 3}
        canvas={EDITOR_CANVAS}
        zoomable
        renderTable={(t) => (
          <div
            className={`flex aspect-square select-none flex-col items-center justify-center rounded-full border-2 text-center shadow-sm ${
              t.vip ? "border-gold bg-white" : "border-line bg-white"
            }`}
          >
            <span className={`text-[18px] font-extrabold leading-none ${t.vip ? "text-gold" : ""}`}>
              {t.label}
            </span>
            <span className="text-sub mt-0.5 text-[11px] leading-none">{t.seats} 位</span>
          </div>
        )}
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button onClick={save} disabled={busy || !dirty} className="btn btn-primary px-4 py-2 text-[14px]">
          {busy ? "儲存中…" : "儲存配置"}
        </button>
        <button onClick={reset} className="btn btn-secondary px-4 py-2 text-[14px]">
          重排成格線
        </button>
        {saved && <span className="text-ok-deep text-[13px]">✓ 已儲存，報名者看到的就是這個配置</span>}
        {dirty && !saved && <span className="text-warn-deep text-[13px]">尚未儲存</span>}
        {error && <span className="text-bad-deep text-[13px]">{error}</span>}
      </div>

      <p className="text-sub mt-2 text-[12px] leading-relaxed">
        拖曳桌子排出餐廳的實際格局，儲存後報名者的選位圖就會照這個配置顯示。
        這裡能搬位置、改桌號與每桌人數——但要增減桌數，還是需要重開一場活動。
      </p>

      <div className="border-line mt-5 border-t pt-4">
        <h3 className="text-[14px] font-bold">桌號與人數</h3>
        <div className="mt-2">
          <TableNames code={code} tables={tables} onRenamed={renamed} />
        </div>
      </div>
    </div>
  );
}
